/*
 * Purpose: Classify connector gaps from workflows, plans, and execution errors
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import type { ReadinessRequirement } from '@/services/autoblocks/planReadiness';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import {
  getGmailAccessToken,
  hasGoogleOAuthConfig,
} from '@/services/connectors/gmailOAuth';
import { getMcpServerUrl } from '@/services/connectors/mcp/mcpConnection';
import { useSecretStore } from '@/stores/secretStore';

export type ConnectorFixKind =
  | 'vault_unlock'
  | 'gmail_oauth'
  | 'gmail_inbox'
  | 'gmail_client_id'
  | 'gmail_no_messages'
  | 'openai_api_key'
  | 'linkedin'
  | 'mcp'
  | 'generic_secret';

export interface ConnectorFixIssue {
  kind: ConnectorFixKind;
  title: string;
  message: string;
  nodeId?: string;
  secretName?: string;
  connectorId?: string;
  /** Hint for Gmail search when fixing gmail_api triggers */
  gmailQuery?: string;
}

function gmailTriggerSource(node: Node): 'gmail_api' | 'webhook' {
  const source = String(node.config.source ?? '');
  if (source === 'gmail_api' || node.config.useGmailApi === true) return 'gmail_api';
  return 'webhook';
}

export function planReadinessToConnectorIssue(item: ReadinessRequirement): ConnectorFixIssue {
  if (item.kind === 'secret' && item.secretName === 'GOOGLE_CLIENT_ID') {
    return {
      kind: 'gmail_client_id',
      title: item.label,
      message: item.description,
      secretName: item.secretName,
      connectorId: item.connectorId,
    };
  }
  if (item.connectorId === 'ab.gmail' || item.id.includes('gmail')) {
    if (item.secretName === 'GOOGLE_CLIENT_ID') {
      return {
        kind: 'gmail_client_id',
        title: item.label,
        message: item.description,
        secretName: item.secretName,
        connectorId: 'ab.gmail',
      };
    }
    return {
      kind: item.kind === 'inbox' ? 'gmail_inbox' : 'gmail_oauth',
      title: item.label,
      message: item.description,
      connectorId: 'ab.gmail',
    };
  }
  if (item.connectorId === 'ab.openai' || item.secretName === 'OPENAI_API_KEY') {
    return {
      kind: 'openai_api_key',
      title: item.label,
      message: item.description,
      secretName: 'OPENAI_API_KEY',
      connectorId: 'ab.openai',
    };
  }
  if (item.connectorId === 'ab.linkedin') {
    return {
      kind: 'linkedin',
      title: item.label,
      message: item.description,
      connectorId: 'ab.linkedin',
      secretName: item.secretName,
    };
  }
  if (item.connectorId === 'ab.mcp') {
    return {
      kind: 'mcp',
      title: item.label,
      message: item.description,
      connectorId: 'ab.mcp',
      secretName: item.secretName,
    };
  }
  return {
    kind: 'generic_secret',
    title: item.label,
    message: item.description,
    secretName: item.secretName,
    connectorId: item.connectorId,
  };
}

export function classifyExecutionError(message: string, nodeId?: string): ConnectorFixIssue | null {
  const lower = message.toLowerCase();

  if (lower.includes('unlock vault') || lower.includes('unlock the vault')) {
    return {
      kind: 'vault_unlock',
      title: 'Unlock your vault',
      message,
      nodeId,
    };
  }

  if (
    lower.includes('google_client_id') ||
    lower.includes('oauth web client') ||
    lower.includes('add google_client_id')
  ) {
    return {
      kind: 'gmail_client_id',
      title: 'Add Google OAuth client',
      message,
      nodeId,
      secretName: 'GOOGLE_CLIENT_ID',
      connectorId: 'ab.gmail',
    };
  }

  if (lower.includes('gmail not connected') || lower.includes('use connect gmail')) {
    return {
      kind: 'gmail_oauth',
      title: 'Connect Gmail',
      message,
      nodeId,
      connectorId: 'ab.gmail',
    };
  }

  if (
    lower.includes('gmail inbox not configured') ||
    lower.includes('pending-inbox') ||
    lower.includes('missing inbox id')
  ) {
    return {
      kind: 'gmail_inbox',
      title: 'Create Gmail edge inbox',
      message,
      nodeId,
      connectorId: 'ab.gmail',
    };
  }

  if (lower.startsWith('no gmail messages for query')) {
    const queryMatch = message.match(/query:\s*(.+)$/i);
    return {
      kind: 'gmail_no_messages',
      title: 'No Gmail messages matched',
      message,
      nodeId,
      connectorId: 'ab.gmail',
      gmailQuery: queryMatch?.[1]?.trim(),
    };
  }

  if (lower.includes('mcp server url') || lower.includes('connect → mcp')) {
    return {
      kind: 'mcp',
      title: 'Connect MCP server',
      message,
      nodeId,
      connectorId: 'ab.mcp',
    };
  }

  if (
    lower.includes('openai') &&
    (lower.includes('401') || lower.includes('api key') || lower.includes('incorrect api key'))
  ) {
    return {
      kind: 'openai_api_key',
      title: 'Add OpenAI API key',
      message,
      nodeId,
      secretName: 'OPENAI_API_KEY',
      connectorId: 'ab.openai',
    };
  }

  if (lower.includes('linkedin') && (lower.includes('oauth') || lower.includes('token'))) {
    return {
      kind: 'linkedin',
      title: 'Connect LinkedIn',
      message,
      nodeId,
      connectorId: 'ab.linkedin',
    };
  }

  return null;
}

function workflowNeedsVaultSecrets(workflow: Workflow): boolean {
  for (const node of workflow.nodes) {
    if (node.type === 'tool' && node.subtype === 'http') {
      const url = String(node.config.url ?? '');
      if (url.includes('openai.com') && !hasResolvedSecret('OPENAI_API_KEY')) return true;
    }
    if (node.type === 'trigger' && node.subtype === 'gmail_inbound') {
      if (gmailTriggerSource(node) === 'gmail_api' && !hasGoogleOAuthConfig()) return true;
    }
  }
  return false;
}

export function findWorkflowConnectorIssues(workflow: Workflow): ConnectorFixIssue[] {
  const issues: ConnectorFixIssue[] = [];
  const seen = new Set<string>();

  const push = (issue: ConnectorFixIssue) => {
    const key = `${issue.kind}:${issue.nodeId ?? ''}:${issue.secretName ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push(issue);
  };

  if (!useSecretStore.getState().isUnlocked && workflowNeedsVaultSecrets(workflow)) {
    push({
      kind: 'vault_unlock',
      title: 'Unlock your vault',
      message: 'Secrets are encrypted on this device — unlock to connect Gmail, OpenAI, and other services.',
    });
  }

  for (const node of workflow.nodes) {
    if (node.type === 'trigger' && node.subtype === 'gmail_inbound') {
      const source = gmailTriggerSource(node);
      const query = String(node.config.gmailQuery ?? 'is:unread newer_than:7d');

      if (source === 'gmail_api') {
        if (!hasGoogleOAuthConfig()) {
          push({
            kind: 'gmail_client_id',
            title: 'Google OAuth client required',
            message: 'Add GOOGLE_CLIENT_ID to your vault before connecting Gmail.',
            nodeId: node.id,
            secretName: 'GOOGLE_CLIENT_ID',
            connectorId: 'ab.gmail',
            gmailQuery: query,
          });
        }
        if (!getGmailAccessToken()) {
          push({
            kind: 'gmail_oauth',
            title: 'Connect Gmail',
            message: 'This flow searches your real Gmail inbox — connect OAuth before running live.',
            nodeId: node.id,
            connectorId: 'ab.gmail',
            gmailQuery: query,
          });
        }
      } else {
        const inboxId = String(node.config.inboxId ?? '').trim();
        if (!inboxId || inboxId === 'pending-inbox') {
          push({
            kind: 'gmail_inbox',
            title: 'Gmail edge inbox missing',
            message: 'Create an edge inbox or switch the trigger to Your Gmail (OAuth).',
            nodeId: node.id,
            connectorId: 'ab.gmail',
          });
        }
      }
    }

    if (node.type === 'tool' && node.subtype === 'http') {
      const url = String(node.config.url ?? '');
      if (url.includes('openai.com') && !hasResolvedSecret('OPENAI_API_KEY')) {
        push({
          kind: 'openai_api_key',
          title: 'OpenAI API key required',
          message: 'This HTTP step calls OpenAI — add OPENAI_API_KEY to your vault.',
          nodeId: node.id,
          secretName: 'OPENAI_API_KEY',
          connectorId: 'ab.openai',
        });
      }
    }

    if (node.subtype === 'mcp_tool') {
      const serverId = String(node.config.mcpServerId ?? node.config.serverId ?? 'default');
      if (!getMcpServerUrl(serverId)) {
        push({
          kind: 'mcp',
          title: 'MCP server not configured',
          message: 'Add an MCP server URL and connect before running this block.',
          nodeId: node.id,
          connectorId: 'ab.mcp',
        });
      }
    }
  }

  return issues;
}
